import { useNavigate } from "react-router-dom";

interface ProductCardProps {
  name: string;
  image: string;
  origin: string;
  application: string;
  category: string;
  featured?: boolean;
}

export function ProductCard({ name, image, origin, application, category, featured }: ProductCardProps) {
  return (
    <div className="group bg-white rounded-lg overflow-hidden border border-stone-200 hover:border-stone-300 transition-all duration-300">
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {featured && (
          <span className="absolute top-3 left-3 px-3 py-1 bg-stone-900 text-white text-xs font-sans font-medium rounded-md">
            Featured
          </span>
        )}
        <span className="absolute top-3 right-3 px-3 py-1 bg-white/90 text-stone-700 text-xs font-sans rounded-md">
          {category}
        </span>
      </div>
      <div className="p-4 md:p-5">
        <h3 className="font-serif text-lg md:text-xl font-semibold text-stone-900 mb-1">
          {name}
        </h3>
        <p className="text-stone-500 text-sm font-sans mb-2">
          Origin: {origin}
        </p>
        <p className="text-stone-400 text-xs font-sans">
          {application}
        </p>
      </div>
    </div>
  );
}

interface SectionTitleProps {
  label?: string;
  title: string;
  subtitle?: string;
  align?: "left" | "center";
}

export function SectionTitle({ label, title, subtitle, align = "center" }: SectionTitleProps) {
  const alignClass = align === "center" ? "text-center items-center" : "text-left items-start";
  return (
    <div className={`flex flex-col ${alignClass} mb-10 md:mb-14`}>
      {label && (
        <span className="text-gold-500 text-xs font-sans uppercase tracking-[0.2em] mb-3">
          {label}
        </span>
      )}
      <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 leading-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-stone-500 text-base md:text-lg font-sans max-w-2xl leading-relaxed">
          {subtitle}
        </p>
      )}
    </div>
  );
}

interface CtaButtonProps {
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  onClick?: () => void;
  href?: string;
}

export function CtaButton({ children, variant = "primary", onClick, href }: CtaButtonProps) {
  const baseClasses = "inline-flex items-center gap-2 px-6 py-3 md:px-8 md:py-3.5 font-sans font-medium text-sm rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap";
  const variants = {
    primary: "bg-gold-500 text-white hover:bg-gold-600",
    secondary: "bg-stone-900 text-white hover:bg-stone-800",
    outline: "border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50",
  };

  if (href) {
    return (
      <a href={href} className={`${baseClasses} ${variants[variant]}`}>
        {children}
      </a>
    );
  }

  return (
    <button onClick={onClick} className={`${baseClasses} ${variants[variant]}`}>
      {children}
    </button>
  );
}

export function PageHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="relative bg-stone-900 text-white py-20 md:py-28 lg:py-32">
      <div className="absolute inset-0 opacity-20">
        <img
          src="https://readdy.ai/api/search-image?query=Luxury%20white%20marble%20slab%20texture%20background%20with%20subtle%20grey%20veins%2C%20elegant%20stone%20surface%2C%20professional%20product%20photography%2C%20soft%20studio%20lighting%2C%20clean%20minimal%20background%2C%20high%20resolution%2C%20suitable%20for%20dark%20overlay%20text%20placement%2C%20neutral%20beige%20and%20grey%20tones&width=1600&height=600&seq=1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
          {title}
        </h1>
        {subtitle && (
          <p className="text-stone-300 text-base md:text-lg font-sans max-w-2xl mx-auto">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}